import { useEffect, useMemo, useReducer, useRef } from 'react'
import type { LowerThirdGraphic, PanelGraphic, StageGraphics } from './graphics'
import { serverNow } from './serverClock'

/** What a stage's audience sees over the canvas now: a lower third until its hold runs out, a panel until cleared. */
export interface VisibleGraphics {
  lowerThird: LowerThirdGraphic | null
  panel: PanelGraphic | null
}

export const NO_VISIBLE_GRAPHICS: VisibleGraphics = Object.freeze({ lowerThird: null, panel: null })

/**
 * How long room metadata can take to reach a viewer. A lower third that arrives while the page watches is a moment old
 * at most, so a larger gap between its `shown_at` and the server's clock (as the page knows it) is the clocks
 * disagreeing, not the card being old.
 */
export const PROPAGATION_MS = 5_000

/**
 * When a lower third leaves, on this page's clock, decided once when the page first sees it (`seenAt`), so it never
 * flickers or comes back. Its hold counts from `shown_at` on the server's clock (`serverNow`): a viewer arriving 20 s
 * into a 30 s card sees its last 10 s, and one arriving after it sees nothing. `fresh` (it arrived while the page was
 * watching, rather than in the metadata it opened with) counts an impossible age as none. Never before `seenAt`.
 */
export function lowerThirdDeadline(
  spec: Pick<LowerThirdGraphic, 'holdS' | 'shownAt'>,
  { seenAt, serverNow: serverTime, fresh }: { seenAt: number; serverNow: number; fresh: boolean },
): number {
  const holdMs = Math.max(0, spec.holdS) * 1000
  const shownAt = spec.shownAt ? Date.parse(spec.shownAt) : Number.NaN
  if (!Number.isFinite(shownAt)) return seenAt + holdMs
  let elapsed = Math.max(0, serverTime - shownAt)
  if (fresh && elapsed > PROPAGATION_MS) elapsed = 0
  return seenAt + Math.max(0, holdMs - elapsed)
}

const lowerKeyOf = (spec: LowerThirdGraphic | null | undefined) => (spec ? `${spec.athleteId}|${spec.shownAt ?? ''}|${spec.holdS}` : null)

/**
 * The watched stage's broadcast elements as its audience sees them now (docs/features/badger-cohost.md, Drawing), from
 * the stage's `graphics` in room metadata (`StageLive.graphics`; undefined until the metadata has spoken). A lower third
 * goes once its hold has run out (`lowerThirdDeadline`), and the same card republished with the rest of the metadata
 * keeps its deadline; a new one (a new `shown_at`) starts its own. The panel stays until cleared or replaced. Another
 * stage starts afresh.
 */
export function useStageGraphics(stageId: string, graphics: StageGraphics | undefined): VisibleGraphics {
  const [, rerender] = useReducer((count: number) => count + 1, 0)
  const book = useRef<{ stageId: string; seen: boolean; deadlines: Map<string, number> }>({ stageId, seen: false, deadlines: new Map() })
  if (book.current.stageId !== stageId) book.current = { stageId, seen: false, deadlines: new Map() }

  const lower = graphics?.lowerThird ?? null
  const lowerKey = lowerKeyOf(lower)
  let deadline: number | null = null
  if (lower && lowerKey) {
    deadline = book.current.deadlines.get(lowerKey) ?? null
    if (deadline === null) {
      deadline = lowerThirdDeadline(lower, { seenAt: Date.now(), serverNow: serverNow(), fresh: book.current.seen })
      // Only the card on screen is remembered: an older one never comes back (its shown_at won't be sent again).
      book.current.deadlines = new Map([[lowerKey, deadline]])
    }
  }
  // From the first metadata on, what arrives arrived while the page was watching.
  if (graphics !== undefined) book.current.seen = true

  const showing = deadline !== null && Date.now() < deadline
  useEffect(() => {
    if (deadline === null) return
    const wait = deadline - Date.now()
    if (wait <= 0) return
    const timer = window.setTimeout(rerender, wait)
    return () => window.clearTimeout(timer)
  }, [deadline])

  const panel = graphics?.panel ?? null
  const shownLower = showing ? lower : null
  return useMemo(
    () => (shownLower || panel ? { lowerThird: shownLower, panel } : NO_VISIBLE_GRAPHICS),
    // The specs are new objects with each metadata update; their content is what counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lowerKey, showing, panel && JSON.stringify(panel)],
  )
}
